import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import { appHref } from '@/utils/appHref';
import type {
  AnswerTrialFeedbackRequest,
  AnswerTrialFeedbackResponse,
  TrialFeedbackAnswer,
} from '@/types/backendCallables';

/**
 * /trial-feedback — where a tap on the last-day trial letter's "What is the
 * main thing in the way right now?" lands (`?t=<signed token>&a=<answer>`).
 *
 * The tap in the letter IS the answer, so the page records it on load through
 * the public `answerTrialFeedback` callable (one doc per learner, latest answer
 * wins), says thank you, and offers an optional note saved to the same doc.
 * Unlike /reactivate there is nothing to protect behind a confirm button: the
 * worst a link scanner that runs page JS can do is overwrite an answer.
 *
 * "Not ready to study yet" is pointed at the Settings pause card
 * (/settings?action=pause). The token never signs anyone in, so that page asks
 * them to sign in first if they are not.
 */

const ANSWERS: readonly TrialFeedbackAnswer[] = ['price', 'not_ready', 'not_sure_fit', 'other'];
const NOTE_MAX = 2000;

const THANKS: Record<TrialFeedbackAnswer, string> = {
  price:
    'Thank you for telling me. If $129 is the hard part right now, reply to my email and tell me where you are. I read those myself.',
  not_ready:
    'That is a good reason, and more common than you would think. You can pause our emails for 1, 3 or 6 months and pick this up when you are ready.',
  not_sure_fit:
    'Thank you, that is worth knowing. If you have a minute, tell me a little about how you like to study.',
  other: 'Thank you. If you have a minute, tell me what it is.',
};

type PageStatus = 'saving' | 'recorded' | 'error';
type NoteStatus = 'idle' | 'sending' | 'sent' | 'error';

const TrialFeedbackPage: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get('t') ?? '';
  const rawAnswer = params.get('a') ?? '';
  const answer = (ANSWERS as readonly string[]).includes(rawAnswer) ? (rawAnswer as TrialFeedbackAnswer) : null;

  const [status, setStatus] = useState<PageStatus>(token && answer ? 'saving' : 'error');
  const [note, setNote] = useState('');
  const [noteStatus, setNoteStatus] = useState<NoteStatus>('idle');
  const recordedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!token || !answer) return;
    const key = `${token}:${answer}`;
    if (recordedFor.current === key) return; // StrictMode runs effects twice in dev
    recordedFor.current = key;

    const record = httpsCallable<AnswerTrialFeedbackRequest, AnswerTrialFeedbackResponse>(functions, 'answerTrialFeedback');
    record({ token, answer })
      .then(() => setStatus('recorded'))
      .catch(() => setStatus('error'));
  }, [token, answer]);

  const sendNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!answer || !note.trim()) return;
    setNoteStatus('sending');
    try {
      const record = httpsCallable<AnswerTrialFeedbackRequest, AnswerTrialFeedbackResponse>(functions, 'answerTrialFeedback');
      await record({ token, answer, note: note.trim() });
      setNoteStatus('sent');
    } catch (_err) {
      setNoteStatus('error');
    }
  };

  if (status === 'saving') {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-neutral-600">One moment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-3xl border-2 border-neutral-200 shadow-ledge p-6">
        {status === 'error' || !answer ? (
          <div className="text-center">
            <h2 className="font-display text-xl font-semibold text-neutral-900">This link didn't work</h2>
            <p className="mt-2 text-neutral-600">
              It may have expired. You can still reply to the email and tell me what is in the way, or write to{' '}
              <a href="mailto:pam@socialworktestprepacademy.com" className="font-bold text-primary-600">
                pam@socialworktestprepacademy.com
              </a>
              .
            </p>
          </div>
        ) : (
          <>
            <h2 className="font-display text-xl font-semibold text-neutral-900">Got it, thank you</h2>
            <p className="mt-2 text-neutral-600">{THANKS[answer]}</p>

            {answer === 'not_ready' && (
              <a
                href={appHref('/settings?action=pause')}
                className="mt-5 inline-block w-full rounded-2xl bg-primary-600 px-4 py-3 text-center font-bold text-white transition-colors hover:bg-primary-700"
              >
                Pause my emails
              </a>
            )}

            {noteStatus === 'sent' ? (
              <p className="mt-6 text-sm font-bold text-neutral-700" role="status">
                Thank you, I have it.
              </p>
            ) : (
              <form onSubmit={sendNote} className="mt-6">
                <label htmlFor="trial-feedback-note" className="block text-sm font-bold text-neutral-700">
                  Anything you want to add? (optional)
                </label>
                <textarea
                  id="trial-feedback-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={NOTE_MAX}
                  rows={4}
                  className="mt-2 w-full rounded-2xl border-2 border-neutral-200 p-3 text-neutral-800 focus:border-primary-500 focus:outline-none"
                />
                {noteStatus === 'error' && (
                  <p className="mt-2 text-sm text-red-600" role="alert">That didn't send. Please try again.</p>
                )}
                <button
                  type="submit"
                  disabled={!note.trim() || noteStatus === 'sending'}
                  className="mt-3 w-full rounded-2xl border-2 border-neutral-300 bg-white px-4 py-3 font-bold text-neutral-800 transition-colors hover:bg-neutral-50 disabled:opacity-50"
                >
                  {noteStatus === 'sending' ? 'Sending...' : 'Send'}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TrialFeedbackPage;
